import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';
import { faqList } from '@/mocks/faq';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      return;
    }
    setFormStatus('loading');
    setFormMessage('');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9q4iq6e3oq4jqumtg80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const brandImageUrl = 'https://readdy.ai/api/search-image?query=Elegant%20luxury%20abalone%20gift%20workshop%20with%20wooden%20craftsman%20table%20and%20premium%20gift%20boxes%20displayed%2C%20warm%20golden%20lighting%2C%20artisan%20craftsmanship%20scene%2C%20dark%20rich%20wood%20tones%2C%20sophisticated%20brand%20heritage%20atmosphere%2C%20high%20end%20product%20photography&width=1200&height=700&seq=about-brand-story&orientation=landscape';

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '鲍鱼盒子',
    url: 'https://www.baoyuhezi.com',
    description: '鲍鱼盒子专注高端鲍鱼礼盒定制，精选优质鲍鱼，搭配精美礼盒包装。',
    foundingDate: '2015',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '世纪大道100号',
      addressLocality: '上海',
      addressRegion: '浦东新区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      areaServed: 'CN',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16 md:pt-20">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <section className="w-full px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于鲍鱼盒子
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                源自对品质的执着追求，致力于将全球最优质的鲍鱼以最精美的方式呈现给您
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>
                      鲍鱼盒子创立于2015年，品牌创始团队深耕高端海产品行业二十余年。我们深知，一份好的礼物不仅仅是物品本身，更是心意的传递和品位的彰显。
                    </p>
                    <p>
                      于是，我们走遍全球优质鲍鱼产地——从日本岩手县的三陆海岸，到南非的好望角海域，从澳大利亚南部的塔斯马尼亚，到新西兰的马尔堡峡湾——精选每一只品质上乘的鲍鱼，只为将这份来自深海的珍贵馈赠，以最完美的方式呈现给您。
                    </p>
                    <p>
                      在包装设计上，我们邀请知名设计师操刀，采用黑檀木、真皮、丝绸等高端材质，融合东方美学与现代极简设计理念，让每一款礼盒都成为一件值得珍藏的艺术品。
                    </p>
                    <p>
                      十年来，鲍鱼盒子已服务超过50,000位客户，覆盖商务送礼、节日馈赠和家庭享用三大场景，赢得了广泛的好评与信赖。我们始终坚持"品质为先、诚意至上"的品牌理念，用心做好每一份鲍鱼礼盒。
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={brandImageUrl}
                    alt="鲍鱼盒子品牌故事"
                    title="鲍鱼盒子 - 高端鲍鱼礼盒品牌"
                    className="w-full aspect-[4/3] object-cover object-top rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Brand Values */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12 md:mb-16">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  品牌理念
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-5">
                    <i className="ri-global-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">全球甄选</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    足迹遍布全球30+优质鲍鱼产地，严格筛选每一只鲍鱼，确保源头品质可追溯。
                  </p>
                </div>
                <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600 mb-5">
                    <i className="ri-pen-nib-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">匠心设计</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    知名设计师团队倾力打造，融合传统与现代美学，每一款包装都经过数十次打磨。
                  </p>
                </div>
                <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-600 mb-5">
                    <i className="ri-heart-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">诚意至上</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    以传递心意为己任，用高品质的产品和服务，让每一份礼物都承载真挚的情感。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12 md:mb-16">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  客户口碑
                </h2>
                <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                  来自真实客户的好评与信赖，是我们不断前行的动力
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-7">
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className={`w-4 h-4 flex items-center justify-center ${i < t.rating ? 'text-primary-500' : 'text-foreground-300'}`}>
                          <i className="ri-star-fill text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                      "{t.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-semibold text-sm">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-400">{t.title} · {t.company}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                {/* Contact Info */}
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                    联系我们
                  </h2>
                  <p className="text-sm md:text-base text-foreground-500 mb-8 leading-relaxed">
                    无论您有礼盒定制需求、企业团购咨询，还是任何其他问题，我们都随时为您服务。
                  </p>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">客服热线</p>
                        <p className="text-sm text-foreground-500">400-888-9999</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                        <p className="text-sm text-foreground-500">service@baoyuhezi.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">公司地址</p>
                        <p className="text-sm text-foreground-500">上海市浦东新区陆家嘴金融贸易区世纪大道100号</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">服务时间</p>
                        <p className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                    在线留言
                  </h2>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8 space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                          姓名 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-2">
                          电话 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="contact-phone"
                          name="phone"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的电话"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请描述您的需求，我们将尽快回复"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-honeypot"
                    />

                    {formMessage && (
                      <div className={`text-sm p-3 rounded-lg ${
                        formStatus === 'success' ? 'bg-primary-50 text-primary-700' : 'bg-accent-50 text-accent-700'
                      }`}>
                        {formMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}